import { z } from 'zod'

import { ConceptChipAchievementSchema } from '~/@entities/Achievement'
import { CasAssignmentStatusSchema, CasTypeSchema } from '~/@entities/Cas'
import { ConceptChipSchema, ConceptChipTypeSchema } from '~/@entities/ConceptChip'
import { AccessModifierToStudentSchema } from '~/@entities/Content'
import { ExamScheduleProgressSchema, ExamTypeSchema } from '~/@entities/Exam'
import {
  AllGradeSubjectSchema,
  GradeSchema,
  HighSchoolSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { LearningStatusSchema } from '~/@entities/Worksheet'

// 학생 홈·학부모 홈 화면이 모아 보는 학습 요약. 모양은 2026-10-16 백엔드 develop(b1add2a8c) 의
// studentHome/model/dto·controller/response 와 studentHistory/model/dto/StudentSummaryDto.kt 로 확인했다.

/** 최근 학습 목록을 거르는 종류 */
export const RECENT_STUDY_TYPE = [
  'ALL',
  'HOMEWORK',
  'WORKSHEET',
  'WORKBOOK',
  'EXAM',
  'CAS',
] as const
export const RecentStudyTypeSchema = z.enum(RECENT_STUDY_TYPE)
export type RecentStudyType = z.infer<typeof RecentStudyTypeSchema>

/***************** 주간 정보 *****************/
export const WeeklyInfoSchema = z.object({
  /** 분 */
  totalLearningTime: z.number(),
  problemsSolvedCount: z.number(),
  bestIncreasedGrade: z.array(ConceptChipAchievementSchema),
  bestGradeAchieved: z.array(ConceptChipAchievementSchema),
})
export type WeeklyInfo = z.infer<typeof WeeklyInfoSchema>

/***************** 최근 학습 *****************/
// 학습지·교재·숙제·경시대회·반응형평가를 한 목록으로 모은 한 칸. 종류에 따라 채워지는 id 묶음이 다르다.
// 서버가 함께 내는 filterType·examProgress 는 앱이 읽지 않아 받지 않는다.
export const RecentStudyItemSchema = z.object({
  type: RecentStudyTypeSchema.exclude(['ALL']),
  id: z.number(),
  datetime: z.iso.datetime({ local: true }),
  status: LearningStatusSchema,
  accessModifierToStudent: AccessModifierToStudentSchema,
  isAutoScored: z.boolean(),
  /** 서술형(AI 채점) 학습지 여부 */
  isEssay: z.boolean(),
  /** 서술형 채점 공개 여부 — 서술형이 아니면 null */
  essayScoringOpened: z.boolean().nullable(),
  title: z.string(),
  revision: RevisionSchema.nullable(),
  school: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  existsHomework: z.boolean(),
  examType: ExamTypeSchema.nullable(),
  examScheduleProgress: ExamScheduleProgressSchema.nullable(),
  isSignatureWorkbook: z.boolean().nullable(),
  problemsCount: z.number(),
  score: z.number().nullable(),
  workbookPage: z.string().nullable(),
  workbookThumbnailImageUrl: z.string().nullable(),
  studentWorksheetId: z.number().nullable(),
  studentWorkbookId: z.number().nullable(),
  studentWorkbookProgressId: z.number().nullable(),
  studentWorkbookRevisionId: z.number().nullable(),
  studentWorkbookProgressPageNumber: z.number().nullable(),
  studentWorkbookRevisionRound: z.number().nullable(),
  studentExamId: z.number().nullable(),
  studentExamOpenDatetime: z.iso.datetime({ local: true }).nullable(),
  studentExamSolveDatetime: z.iso.datetime({ local: true }).nullable(),
  assignedCasId: z.number().nullable(),
  casId: z.number().nullable(),
  casType: CasTypeSchema.nullable(),
  casRevision: RevisionSchema.nullable(),
  assignedCasStatus: CasAssignmentStatusSchema.nullable(),
  casStartedAt: z.iso.datetime({ local: true }).nullable(),
  casCompletedAt: z.iso.datetime({ local: true }).nullable(),
})
export type RecentStudyItem = z.infer<typeof RecentStudyItemSchema>

/** 다음 쪽을 이어 받을 커서. 서버가 마지막으로 낸 종류별 id 를 그대로 다음 요청에 싣는다 */
export const RecentStudyCursorSchema = z.object({
  materialViewType: RecentStudyTypeSchema,
  lastStudentWorkbookRevisionId: z.number().nullable(),
  lastStudentWorksheetId: z.number().nullable(),
  lastStudentExamId: z.number().nullable(),
  lastStudentHomeworkId: z.number().nullable(),
  lastAssignedCasId: z.number().nullable(),
})
export type RecentStudyCursor = z.infer<typeof RecentStudyCursorSchema>

export const RecentStudiesSchema = z.object({
  contents: z.array(RecentStudyItemSchema),
  pageable: RecentStudyCursorSchema,
})
export type RecentStudies = z.infer<typeof RecentStudiesSchema>

/***************** 최근 챌린지 *****************/
// 유형칩 종류(개념·기본·심화)마다 학습한 칩 수
const ConceptChipCountByTypeSchema = z.record(ConceptChipTypeSchema, z.number())

export const RecentChallengeItemSchema = z.object({
  littleChapterId: z.number(),
  littleChapterName: z.string(),
  learningConceptChip: ConceptChipCountByTypeSchema,
  conceptChips: z.array(ConceptChipAchievementSchema),
})
export type RecentChallengeItem = z.infer<typeof RecentChallengeItemSchema>

// 서버가 함께 내는 기준 학습지·참조 id 는 앱이 읽지 않아 받지 않는다
export const RecentChallengeSchema = z.object({
  contents: z.array(RecentChallengeItemSchema),
  default: z.object({
    littleChapterId: z.number(),
    /** 항목별이 아니라 목록 전체의 트랙 — 서버가 최신 챌린지 기준으로 분기해 내려준다 */
    isArithmetic: z.boolean(),
  }),
})
export type RecentChallenge = z.infer<typeof RecentChallengeSchema>

/** 강점·약점 유형칩. 아직 성취도가 없으면 null 이다 */
export const StrengthsAndWeaknessesSchema = z.object({
  strengths: ConceptChipAchievementSchema.nullable(),
  weaknesses: ConceptChipAchievementSchema.nullable(),
})
export type StrengthsAndWeaknesses = z.infer<typeof StrengthsAndWeaknessesSchema>

/***************** 챌린지 단원 *****************/
// 챌린지가 걸린 학교급·학년. 고등은 학년 대신 과목이 채워진다
export const ChallengeSchoolSchema = z.object({
  schoolType: SchoolTypeSchema,
  grade: GradeSchema.nullable(),
  semester: SemesterSchema,
  subject: HighSchoolSubjectSchema.nullable(),
})
export type ChallengeSchool = z.infer<typeof ChallengeSchoolSchema>

export const CHALLENGE_STATUS = ['INCOMPLETE', 'PROGRESS', 'COMPLETE'] as const
export const ChallengeStatusSchema = z.enum(CHALLENGE_STATUS)
export type ChallengeStatus = z.infer<typeof ChallengeStatusSchema>

/** 선생님이 학생에게 걸어 둔 스마일 챌린지 단원 */
export const AssignedChallengeSchema = z.object({
  status: ChallengeStatusSchema,
  littleChapterId: z.number(),
  littleChapterName: z.string(),
  revision: RevisionSchema,
  school: ChallengeSchoolSchema.nullable(),
  conceptChipId: z.number().nullable(),
  challengeWorksheetId: z.number().nullable(),
  learningConceptChip: ConceptChipCountByTypeSchema,
})
export type AssignedChallenge = z.infer<typeof AssignedChallengeSchema>

/** 최근 챌린지한 교육과정과 그 단원들의 유형칩 */
export const RecentChallengeCurriculumSchema = z.object({
  recentlyLittleChapterId: z.number(),
  curriculum: z.object({
    revision: RevisionSchema,
    school: ChallengeSchoolSchema.nullable(),
  }),
  littleChapterConceptChips: z.array(
    z.object({
      littleChapterId: z.number(),
      littleChapterName: z.string(),
      littleChapterCurriculumKey: z.string().nullable(),
      conceptChips: z.array(ConceptChipSchema),
    }),
  ),
})
export type RecentChallengeCurriculum = z.infer<typeof RecentChallengeCurriculumSchema>

/***************** 주간 학습 통계 *****************/
// 4주 차트용. date 는 그 주가 속한 연월(YearMonth, "2026-04")이다
export const WeeklyLearningSummarySchema = z.object({
  date: z.string(),
  week: z.number(),
  weekStartDate: z.iso.date(),
  weekEndDate: z.iso.date(),
  /** 정답률 0~100 */
  rate: z.number(),
  totalCount: z.number(),
  wrongCount: z.number(),
  correctCount: z.number(),
  worksheetProblemCount: z.number(),
  workbookProblemCount: z.number(),
})
export type WeeklyLearningSummary = z.infer<typeof WeeklyLearningSummarySchema>
